/*
 * Mara League OS — Notification Bell (Phase 6)
 * --------------------------------------------
 * Header widget: bell icon with unread badge + popover feed of the latest
 * 10 notifications, mark-all-read shortcut, "View all" link to /notifications.
 * Polls unread count every 20s.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, ChevronRight, Circle, Zap, Gavel, UserPlus, Cog, Trophy } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";

const POLL_MS = 20_000;

const CATEGORY_ICON = {
  match: Trophy,
  auction: Gavel,
  registration: UserPlus,
  platform: Cog,
};
const CATEGORY_COLOR = {
  match: "text-red-400",
  auction: "text-amber-400",
  registration: "text-emerald-400",
  platform: "text-sky-400",
};
const PRIORITY_ACCENT = {
  high: "border-l-red-500",
  normal: "border-l-emerald-600",
  low: "border-l-zinc-600",
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);

  const loadCount = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.unread || 0);
    } catch { /* silent */ }
  }, [user]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 10 } });
      setItems(data.items || []);
      setUnread(data.unread_total || 0);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  // Poll unread count
  useEffect(() => {
    if (!user) return;
    loadCount();
    const iv = setInterval(loadCount, POLL_MS);
    return () => clearInterval(iv);
  }, [user, loadCount]);

  // Load items when popover opens
  useEffect(() => { if (open) loadItems(); }, [open, loadItems]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markAll = async () => {
    try {
      await api.post("/notifications/mark-read", { all: true });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* silent */ }
  };

  const clickItem = async (n) => {
    if (!n.read) {
      try {
        await api.post("/notifications/mark-read", { ids: [n.id] });
        setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
        setUnread((u) => Math.max(0, u - 1));
      } catch { /* silent */ }
    }
    setOpen(false);
    if (n.url) nav(n.url);
  };

  const displayCount = useMemo(() => unread > 99 ? "99+" : String(unread), [unread]);
  if (!user) return null;

  return (
    <div className="relative" ref={rootRef}>
      <button
        data-testid="notification-bell"
        className="relative w-8 h-8 rounded-md flex items-center justify-center hover:bg-white/5 transition-colors"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4 text-zinc-300" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[9px] font-bold flex items-center justify-center animate-pulse">
            {displayCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 z-40 w-96 max-w-[95vw] rounded-md border border-[#16322a] bg-[#0d1c15] shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-bold text-white uppercase tracking-widest">Notifications</span>
              {unread > 0 && <Badge className="bg-red-600 text-white h-4 px-1.5 text-[9px]">{unread}</Badge>}
            </div>
            {unread > 0 && (
              <button
                onClick={markAll}
                data-testid="notif-mark-all"
                className="text-[10px] uppercase tracking-widest text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
              >
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="p-6 text-center text-zinc-500 text-xs">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-zinc-500 text-xs">
                <Bell className="w-8 h-8 mx-auto text-zinc-700 mb-2" />
                You&apos;re all caught up. Notifications will show here as matches, auctions, and platform activity happen.
              </div>
            ) : (
              items.map((n) => {
                const Icon = CATEGORY_ICON[n.category] || Zap;
                return (
                  <button
                    key={n.id}
                    onClick={() => clickItem(n)}
                    data-testid={`notif-${n.id}`}
                    className={`w-full text-left px-4 py-3 border-b border-white/5 border-l-2 ${PRIORITY_ACCENT[n.priority] || "border-l-transparent"} hover:bg-black/40 transition-colors ${n.read ? "opacity-60" : ""}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 ${CATEGORY_COLOR[n.category] || "text-white"}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="text-sm text-white font-medium truncate">{n.title}</div>
                          {!n.read && <Circle className="w-1.5 h-1.5 fill-emerald-400 text-emerald-400" />}
                        </div>
                        {n.body && <div className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{n.body}</div>}
                        <div className="text-[10px] text-zinc-600 mt-1">{timeAgo(n.created_at)} · {n.category}</div>
                      </div>
                      {n.url && <ChevronRight className="w-4 h-4 text-zinc-600 shrink-0 mt-1" />}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <button
            onClick={() => { setOpen(false); nav("/notifications"); }}
            className="w-full py-2.5 text-center text-xs text-emerald-400 hover:text-emerald-300 uppercase tracking-widest border-t border-white/5"
            data-testid="notif-view-all"
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
}
